import { useEffect, useState } from 'react';

interface BetaInstallerUrlResponse {
  url: string | null;
}

// Per-request read of BETA_INSTALLER_URL through /api/beta-installer-url, so the URL can change
// with `.env` + `pm2 reload` and no rebuild. Consumed only by the BetaDownloadButton of the
// trial-beta LP (currently disabled); the site's official installer comes from
// /api/installer-url through LinksMirrorDownload instead. `enabled` gates the fetch.
//
// Three states, distinguished on purpose: `undefined` means the fetch hasn't settled yet
// (callers must NOT fall back to the stable installer here — that would silently hand a beta
// participant the wrong .exe for however long the round trip takes); `null` means it settled
// and the env var is genuinely unset (callers may fall back to the official installer); a string
// is the real URL.
//
// Same budget as LinksMirrorDownload's probe. It exists because `fetch` has no deadline of its
// own: a request that neither resolves nor rejects — a stalled mobile connection, a proxy
// black-holing the route — would otherwise hold this at `undefined` forever, and `undefined` is
// exactly what keeps a caller's download control inert. Timing out into `null` is the difference
// between a control that falls back to the stable installer and one that never works again.
const TIMEOUT_MS = 3000;

export function useBetaInstallerUrl(enabled: boolean): string | null | undefined {
  const [installerUrl, setInstallerUrl] = useState<string | null | undefined>(undefined);

  useEffect(() => {
    if (!enabled) return undefined;

    const controller = new AbortController();

    // Settles the state itself rather than relying on the abort to reject the request: aborting
    // is what stops the round trip, but nothing guarantees the rejection lands.
    const timeout = setTimeout(() => {
      controller.abort();
      setInstallerUrl(null);
    }, TIMEOUT_MS);

    // Guards environments where `fetch` itself throws synchronously instead of the request
    // failing asynchronously (older test/runtime setups without a fetch polyfill) — same
    // "settled, nothing configured" fallback as a rejected request.
    try {
      fetch('/api/beta-installer-url', { signal: controller.signal })
        .then((response) => (response.ok ? (response.json() as Promise<BetaInstallerUrlResponse>) : { url: null }))
        .then((data) => {
          clearTimeout(timeout);
          setInstallerUrl(data.url);
        })
        .catch(() => {
          clearTimeout(timeout);
          setInstallerUrl(null);
        });
    } catch {
      clearTimeout(timeout);
      setInstallerUrl(null);
    }

    return () => {
      clearTimeout(timeout);
      controller.abort();
    };
  }, [enabled]);

  return installerUrl;
}
